import { InvoiceView } from "src/api/generated/erp/db/types/tables/invoiceView";
import { InvoiceUpdatingApi } from "src/api/generated/erp/sales/invoicing/api/invoiceUpdatingApi.ts";
import { FormCommonProps } from "src/components/common/forms/types";
import { IsoDateString } from "src/types/dateTime";
import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import i18n from "i18next";
import { FormDateField } from "src/components/common/forms/fields/FormDateField.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { getInvoiceChannelLabels } from "src/api/generated/erp/db/types/enums/invoiceChannel.ts";
import { FormCountrySelectField } from "src/components/common/country/FormCountrySelectField.tsx";
import { Country } from "src/api/generated/io/aavo/applications/db/erp/types/country.ts";
import { Divider } from "@mui/material";
import { DEFAULT_COUNTRY_CODE } from "src/components/common/country/countries.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface InvoiceFormProps extends FormCommonProps<number> {
	invoiceId: number;
}

interface FormValues {
	invoicePrintoutText: string;
	invoiceDate: IsoDateString;
	externalId: string;
	currencyCode: string;
	paidExchangeRate: number | null;
	orderReference: string;
	customerPoNo: string;
	penaltyInterest: number | null;
	remindDays: number | null;
	invoiceChannel: "EMAIL" | "EINVOICE" | "LETTER" | "NO_SENDING";
	deliveryAddressName: string;
	deliveryAddressAddress1: string;
	deliveryAddressAddress2: string;
	deliveryAddressPostalCode: string;
	deliveryAddressCity: string;
	deliveryAddressCountry: Country;
	invoiceAddressName: string;
	invoiceAddressAddress1: string;
	invoiceAddressAddress2: string;
	invoiceAddressPostalCode: string;
	invoiceAddressCity: string;
	invoiceAddressCountry: Country;
}

export const InvoiceForm = ({ invoiceId, onFormEdited, onCompleted }: InvoiceFormProps) => {
	return (
		<AsyncForm<InvoiceView, FormValues, number>
			fetch={() => InvoiceUpdatingApi.getFormInitData({ invoiceId })}
			getDefaultValues={(invoice) => ({
				invoicePrintoutText: invoice.invoicePrintoutText,
				invoiceDate: invoice.invoiceDate,
				externalId: invoice.externalId ?? "",
				currencyCode: invoice.currencyCode,
				paidExchangeRate: invoice.paidExchangeRate ?? null,
				orderReference: invoice.orderReference,
				customerPoNo: invoice.customerPoNo,
				penaltyInterest: invoice.penaltyInterest,
				remindDays: invoice.remindDays,
				invoiceChannel: invoice.invoiceChannel,
				deliveryAddressName: invoice.deliveryAddressName ?? "",
				deliveryAddressAddress1: invoice.deliveryAddressAddress_1 ?? "",
				deliveryAddressAddress2: invoice.deliveryAddressAddress_2 ?? "",
				deliveryAddressPostalCode: invoice.deliveryAddressPostalCode ?? "",
				deliveryAddressCity: invoice.deliveryAddressCity ?? "",
				deliveryAddressCountry: invoice.deliveryAddressCountry ?? DEFAULT_COUNTRY_CODE,
				invoiceAddressName: invoice.invoiceAddressName ?? "",
				invoiceAddressAddress1: invoice.invoiceAddressAddress_1 ?? "",
				invoiceAddressAddress2: invoice.invoiceAddressAddress_2 ?? "",
				invoiceAddressPostalCode: invoice.invoiceAddressPostalCode ?? "",
				invoiceAddressCity: invoice.invoiceAddressCity ?? "",
				invoiceAddressCountry: invoice.invoiceAddressCountry ?? DEFAULT_COUNTRY_CODE,
			})}
			onFormEdited={onFormEdited}
			onCompleted={onCompleted}
			submit={submitForm}
			columns={3}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.invoice.form"),
				title: i18n.t("invoice"),
				pathSegments: [i18n.t("form")],
				data: { invoiceId },
			}}
			render={(params) => <InvoiceFormContent {...params} />}
		/>
	);

	function submitForm(
		values: FormValues,
		{ data: invoice }: AsyncFormContentParams<InvoiceView, FormValues>,
	) {
		return (async () => {
			await InvoiceUpdatingApi.update({
				request: {
					invoiceId: invoice.invoiceId,
					lastUpdated: invoice.invoiceLastUpdated,
					invoicePrintoutText: values.invoicePrintoutText,
					invoiceDate: values.invoiceDate,
					externalId: values.externalId || null,
					orderReference: values.orderReference,
					customerPoNo: values.customerPoNo,
					penaltyInterest: values.penaltyInterest ?? 0,
					remindDays: values.remindDays ?? 0,
					invoiceChannel: values.invoiceChannel,
					deliveryAddressName: values.deliveryAddressName,
					deliveryAddressAddress1: values.deliveryAddressAddress1,
					deliveryAddressAddress2: values.deliveryAddressAddress2,
					deliveryAddressPostalCode: values.deliveryAddressPostalCode,
					deliveryAddressCity: values.deliveryAddressCity,
					deliveryAddressCountry: values.deliveryAddressCountry,
					invoiceAddressName: values.invoiceAddressName,
					invoiceAddressAddress1: values.invoiceAddressAddress1,
					invoiceAddressAddress2: values.invoiceAddressAddress2,
					invoiceAddressPostalCode: values.invoiceAddressPostalCode,
					invoiceAddressCity: values.invoiceAddressCity,
					invoiceAddressCountry: values.invoiceAddressCountry,
				},
			});
			return invoice.invoiceId;
		})();
	}
};

interface InvoiceFormContentProps extends AsyncFormContentParams<InvoiceView, FormValues> {}

const InvoiceFormContent = ({ control, data: invoice }: InvoiceFormContentProps) => (
	<>
		<FormTextField control={control} name={"externalId"} label={i18n.t("external_id")} />
		<FormTextField control={control} name={"currencyCode"} label={i18n.t("currency")} disabled />
		<FormNumberField control={control} name={"paidExchangeRate"} label={i18n.t("exchange_rate")} disabled />
		<FormDateField
			control={control}
			name={"invoiceDate"}
			label={i18n.t("invoice_date")}
			rules={requireRule()}
			disabled={invoice.invoiceState !== "INITIAL"}
		/>
		<FormTextField control={control} name={"orderReference"} label={i18n.t("order_reference")} />
		<FormTextField control={control} name={"customerPoNo"} label={i18n.t("customer_po_no")} />
		<FormEnumSelectField
			control={control}
			name={"invoiceChannel"}
			label={i18n.t("invoice_channel")}
			options={getInvoiceChannelLabels()}
			disableClearable
		/>
		<FormNumberField control={control} name={"penaltyInterest"} label={i18n.t("penalty_interest")} />
		<FormNumberField control={control} name={"remindDays"} label={i18n.t("remind_days")} />
		<FormTextField
			control={control}
			name={"invoicePrintoutText"}
			label={i18n.t("invoice_printout_text")}
			multiline={true}
			minRows={2}
			spanGridColumns
		/>

		<Divider
			sx={{
				gridColumn: "1 / -1",
				color: "primary.main",
			}}
		>
			{i18n.t("delivery_address")}
		</Divider>
		<FormTextField control={control} name={"deliveryAddressName"} label={i18n.t("address_description")} />
		<FormTextField control={control} name={"deliveryAddressAddress1"} label={i18n.t("street_address")} />
		<FormTextField control={control} name={"deliveryAddressAddress2"} label={i18n.t("street_address_2")} />
		<FormTextField control={control} name={"deliveryAddressPostalCode"} label={i18n.t("postal_code")} />
		<FormTextField control={control} name={"deliveryAddressCity"} label={i18n.t("city")} />
		<FormCountrySelectField control={control} name={"deliveryAddressCountry"} label={i18n.t("country")} />

		<Divider
			sx={{
				gridColumn: "1 / -1",
				color: "primary.main",
			}}
		>
			{i18n.t("invoice_address")}
		</Divider>
		<FormTextField control={control} name={"invoiceAddressName"} label={i18n.t("address_description")} />
		<FormTextField control={control} name={"invoiceAddressAddress1"} label={i18n.t("street_address")} />
		<FormTextField control={control} name={"invoiceAddressAddress2"} label={i18n.t("street_address_2")} />
		<FormTextField control={control} name={"invoiceAddressPostalCode"} label={i18n.t("postal_code")} />
		<FormTextField control={control} name={"invoiceAddressCity"} label={i18n.t("city")} />
		<FormCountrySelectField control={control} name={"invoiceAddressCountry"} label={i18n.t("country")} />
	</>
);
